import React, { useState, useRef, useEffect } from 'react'
import { useMediaQuery } from '@material-ui/core'

import './dropDownHL.css'
import TooltipHLPure from '../tooltipHL/tooltipHL.pure'
import { FilterIcon } from '../../../assets/logos/icons'

/**
 * @author Marissa Vargas
 * @date 2025-04-22
 * @description This is a customized component to show in desktop devices only. Its main objective is to limit the amount of options displayed when the list of options is too big. It imitates HTML native behavior, such as keyboard features and screen reader support.
 * @param {Object} props The properties passed to the component.
 * @param {string} props.labelDropDown The label for the dropdown.
 * @param {Array} props.optionsDropDown The options for the dropdown.
 * @param {function} props.onDropDownChange The function to call when the dropdown value changes.
 * @param {(string|number)} props.optionsValue The value of the selected option.
 * @param {string} props.valueItem The key to use for the value in the options array.
 * @param {string} props.nameOption The key to use for the display name in the options array.
 * @param {boolean} [props.flex] Whether to use flexbox for layout.
 * @param {boolean} [props.tooltipShow] Whether to show the tooltip.
 * @param {'top'|'left'|'right'|'bottom'} [props.positionTooltip] The position of the tooltip.
 * @param {string} [props.titleTooltip] The title of the tooltip.
 * @param {string} [props.descriptionTooltip] The description of the tooltip.
 * @param {string} [props.subContent] Additional content for the tooltip.
 * @param {string} [props.restContent] Additional content for the tooltip.
 * @param {boolean} [props.informationIcon] Whether to show the information icon.
 * @param {boolean} [props.disable] Whether the dropdown is disabled.
 * @param {boolean} [props.required] Whether the dropdown is required.
 * @param {'bottom'|'top'} [props.position] The position of the option list, default is 'bottom'.
 * @param {boolean} [props.iconFilter] Whether to show the filter icon or not, default is false.
 * @returns {JSX.Element} The rendered component.
 * @example
 * ```
 *  * jsx
 * <DropDownBtnHLPure
 *  labelDropDown={t('labels.fondo')}
 *  optionsDropDown={[ name: 'Fondo 1', value: 'fondo1' }, { name: 'Fondo 2', value: 'fondo2' }]}
 *  optionsValue={fund}
 *  valueItem={'value'}
 *  nameOption={'name'}
 *  onDropDownChange={(e) =>
 *    setFund(e.target.value)
 *  }
 *  position='top'
 *  iconFilter={true}
 *  required
 * />
 * ```
 */
const DropDownBtnHLPure = (props) => {
  const {
    labelDropDown,
    optionsDropDown,
    onDropDownChange,
    optionsValue,
    valueItem,
    nameOption,
    flex,
    tooltipShow,
    positionTooltip,
    titleTooltip,
    descriptionTooltip,
    subContent,
    restContent,
    informationIcon,
    disable,
    required,
    position = 'bottom',
    iconFilter = false,
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(optionsValue)
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0)
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)
  const optionRefs = useRef([])

  const handleOptionClick = (value, displayValue, index) => {
    setSelectedValue(value)
    setCurrentOptionIndex(index)
    setIsOpen(false)
    onDropDownChange({
      target: {
        value: value,
        id: optionsValue,
        selectedOptions: [{ value, textContent: displayValue }],
      },
    })
  }

  const handleToggleDropdown = () => {
    if (!disable) {
      setIsOpen((prev) => !prev)
    }
  }

  const handleKeyPress = (event) => {
    const { key } = event
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']

    if (!isOpen && openKeys.includes(key)) {
      event.preventDefault()
      setIsOpen(true)
    } else if (isOpen) {
      switch (key) {
        case 'Escape':
          event.preventDefault()
          setIsOpen(false)
          break
        case 'ArrowDown':
          event.preventDefault()
          moveFocusDown()
          break
        case 'ArrowUp':
          event.preventDefault()
          moveFocusUp()
          break
        case 'Enter':
          event.preventDefault()
          selectCurrentOption()
          break
        default:
          break
      }
    }
  }

  const moveFocusDown = () => {
    setCurrentOptionIndex((prevIndex) =>
      prevIndex < optionsDropDown.length - 1 ? prevIndex + 1 : 0
    )
  }

  const moveFocusUp = () => {
    setCurrentOptionIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : optionsDropDown.length - 1
    )
  }

  const selectCurrentOption = () => {
    const currentOption = optionsDropDown[currentOptionIndex]
    const value = valueItem ? currentOption[valueItem] : currentOption
    const displayValue = currentOption[nameOption] || currentOption
    handleOptionClick(value, displayValue, currentOptionIndex)
  }

  useEffect(() => {
    if (isOpen && optionRefs.current[currentOptionIndex]) {
      optionRefs.current[currentOptionIndex].scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
      })
    }
  }, [currentOptionIndex, isOpen])

  useEffect(() => {
    setSelectedValue(optionsValue)
  }, [optionsValue])

  useEffect(() => {
    const index = optionsDropDown.findIndex(option =>
      valueItem ? option[valueItem] === optionsValue : option === optionsValue
    )
    setCurrentOptionIndex(index >= 0 ? index : 0)
  }, [optionsValue, optionsDropDown, valueItem])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption =
    optionsDropDown.find((option) =>
      valueItem ? option[valueItem] === selectedValue : option === selectedValue
    ) ||
    optionsDropDown[0] ||
    {}

  return (
    <div
      className={
        flex === true
          ? 'dropDownBtnHLPure-containerFlex'
          : 'dropDownBtnHLPure-container'
      }
    > 
      {iconFilter && (
        <div className='filter-icon-div'>
          <img src={FilterIcon} alt='' className='filter-icon-img' />
        </div>
      )}
      <div
        className={
          flex === true
            ? 'dropDownBtnHLPure-container-textFlex'
            : 'dropDownBtnHLPure-container-text'
        }
      >
        <label className='dropDownBtnHLPure-label' htmlFor={optionsValue}>
          {labelDropDown}
          {required && ' *'}
        </label>
        {tooltipShow ? (
          <TooltipHLPure
            positionTooltip={positionTooltip}
            titleTooltip={titleTooltip}
            descriptionTooltip={descriptionTooltip}
            subContent={subContent}
            restContent={restContent}
            informationIcon={informationIcon}
          />
        ) : null}
      </div>
      <div className='dropDownBtnHLPure-select-wrapper' ref={dropdownRef}>
        <div
          ref={buttonRef}
          className={`dropdown-display ${isOpen ? 'dropdown-open' : ''} ${
            disable ? 'dropdown-disable' : ''
          }`}
          onClick={handleToggleDropdown}
          onKeyDown={handleKeyPress}
          role='combobox'
          aria-disabled={disable}
          aria-required={required}
          aria-controls='listbox'
          aria-haspopup='listbox'
          aria-expanded={isOpen}
          tabIndex={0}
        >
          {selectedOption[nameOption] ||
            (typeof selectedOption === 'string' ? selectedOption : '') ||
            (required ? 'Select an option' : '')}
        </div>
        {isOpen && (
          <ul
            role='listbox'
            className={`dropdown-container ${
              optionsDropDown.length >= 8 ? 'dropdown-lg' : ''
            } ${position === 'top' ? 'dropdown-container-top' : ''}`}
          >
            {optionsDropDown.map((item, index) => {
              const value = valueItem ? item[valueItem] : item
              const displayValue = item[nameOption] || item
              return (
                <li
                  ref={(el) => (optionRefs.current[index] = el)}
                  role='option'
                  aria-selected={index === currentOptionIndex}
                  key={index}
                  className={`dropdown-option ${
                    index === currentOptionIndex ? 'option-selected' : ''
                  }`}
                  onClick={() => handleOptionClick(value, displayValue, index)}
                >
                  {displayValue}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

/**
 * @author Alan Aguilar
 * @description A native dropdown component for smaller screens.
 * @param {Object} props The properties passed to the component.
 * @param {string} props.labelDropDown The label for the dropdown.
 * @param {Array} props.optionsDropDown The options for the dropdown.
 * @param {function} props.onDropDownChange The function to call when the dropdown value changes.
 * @param {(string|number)} props.optionsValue The value of the selected option.
 * @param {string} props.valueItem The key to use for the value in the options array.
 * @param {string} props.nameOption The key to use for the display name in the options array.
 * @param {boolean} [props.flex] Whether to use flexbox for layout.
 * @param {boolean} [props.tooltipShow] Whether to show the tooltip.
 * @param {'top'|'left'|'right'|'bottom'} [props.positionTooltip] The position of the tooltip.
 * @param {string} [props.titleTooltip] The title of the tooltip.
 * @param {string} [props.descriptionTooltip] The description of the tooltip.
 * @param {string} [props.subContent] Additional content for the tooltip.
 * @param {string} [props.restContent] Additional content for the tooltip.
 * @param {boolean} [props.informationIcon] Whether to show the information icon.
 * @param {boolean} [props.disable] Whether the dropdown is disabled.
 * @param {boolean} [props.required] Whether the dropdown is required.
  * @param {boolean} [props.iconFilter] Whether to show the filter icon or not, default is false.
 * @returns {JSX.Element} The rendered component.
 * @example
 * ```
 * jsx
 * <DropDownBtnHLPure
 *  labelDropDown={t('labels.fondo')}
 *  optionsDropDown={[ name: 'Fondo 1', value: 'fondo1' }, { name: 'Fondo 2', value: 'fondo2' }]}
 *  optionsValue={fund}
 *  valueItem={'value'}
 *  nameOption={'name'}
 *  onDropDownChange={(e) =>
 *    setFund(e.target.value)
 *  }
 *  required
 * />
 * ```
 */
const DropDownBtnHLPureNative = (props) => {
  const {
    labelDropDown,
    optionsDropDown,
    onDropDownChange,
    optionsValue,
    valueItem,
    nameOption,
    flex,
    tooltipShow,
    positionTooltip,
    titleTooltip,
    descriptionTooltip,
    subContent,
    restContent,
    informationIcon,
    disable,
    required,
    iconFilter = false
  } = props

  return (
    <div
      className={
        flex === true
          ? 'dropDownBtnHLPure-containerFlex'
          : 'dropDownBtnHLPure-container'
      }
    >
      {iconFilter && (
        <div className='filter-icon-div'>
          <img src={FilterIcon} alt='' className='filter-icon-img' />
        </div>
      )}
      <div
        className={
          flex === true
            ? 'dropDownBtnHLPure-container-textFlex'
            : 'dropDownBtnHLPure-container-text'
        }
      >
        <label className='dropDownBtnHLPure-label' for={optionsValue}>
          {labelDropDown}
          {required && ' *'}
        </label>
        {tooltipShow ? (
          <TooltipHLPure
            positionTooltip={positionTooltip}
            titleTooltip={titleTooltip}
            descriptionTooltip={descriptionTooltip}
            subContent={subContent}
            restContent={restContent}
            informationIcon={informationIcon}
          />
        ) : (
          ''
        )}
      </div>
      <div className='dropDownBtnHLPure-select-wrapper'>
        <select
          className='dropDownBtnHLPure-select'
          onChange={onDropDownChange}
          id={optionsValue}
          disabled={disable}
          required={required}
        >
          {optionsDropDown.map((arrayItem, index) => {
            return (
              <option
                value={valueItem !== '' ? arrayItem[valueItem] : arrayItem}
                key={index}
                selected={arrayItem[valueItem] === optionsValue ? true : false}
              >
                {arrayItem[nameOption] || arrayItem}
              </option>
            )
          })}
        </select>
      </div>
    </div>
  )
}

/**
 * @author Marissa Vargas
 * @date 2025-04-22
 * @description A responsive dropdown component that dynamically switches between a custom dropdown for desktop devices and a native dropdown for smaller screens.
 * @param {Object} props The properties passed to the component.
 * @param {string} props.labelDropDown The label for the dropdown.
 * @param {Array} props.optionsDropDown The options for the dropdown.
 * @param {function} props.onDropDownChange The function to call when the dropdown value changes.
 * @param {(string|number)} props.optionsValue The value of the selected option.
 * @param {string} props.valueItem The key to use for the value in the options array.
 * @param {string} props.nameOption The key to use for the display name in the options array.
 * @param {boolean} [props.flex] Whether to use flexbox for layout.
 * @param {boolean} [props.tooltipShow] Whether to show the tooltip.
 * @param {'top'|'left'|'right'|'bottom'} [props.positionTooltip] The position of the tooltip.
 * @param {string} [props.titleTooltip] The title of the tooltip.
 * @param {string} [props.descriptionTooltip] The description of the tooltip.
 * @param {string} [props.subContent] Additional content for the tooltip.
 * @param {string} [props.restContent] Additional content for the tooltip.
 * @param {boolean} [props.informationIcon] Whether to show the information icon.
 * @param {boolean} [props.disable] Whether the dropdown is disabled.
 * @param {boolean} [props.required] Whether the dropdown is required.
 * @param {'bottom'|'top'} [props.position] The position of the option list, default is to 'bottom'.
 * @param {boolean} [props.iconFilter] Whether to show the filter icon or not, default is false.
 * @returns {JSX.Element} The rendered responsive dropdown component.
 * @see DropDownBtnHLPure for desktop dropdown implementation.
 * @see DropDownBtnHLPureNative for native dropdown implementation.
 * @example
 * ```jsx
 * <DropdownPure
 *   labelDropDown='Select an option'
 *   optionsDropDown={[
 *     { name: 'Option 1', value: 'option1' },
 *     { name: 'Option 2', value: 'option2' },
 *   ]}
 *   optionsValue='option1'
 *   valueItem='value'
 *   nameOption='name'
 *   onDropDownChange={(e) => console.log(e.target.value)}
 *   position='top'
 *   required
 * />
 * ```
 */
const DropdownPure = (props) => {
  const matches = useMediaQuery('(min-width:820px)')

  return matches ? (
    <DropDownBtnHLPure {...props} />
  ) : (
    <DropDownBtnHLPureNative {...props} />
  )
}

export { DropdownPure as default }
